<script setup lang="ts">
import { useScriptYouTubePlayer } from '#imports'
import { useIdle } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import VoixVideoControls from './VoixVideoControls.vue'

// Config
const props = defineProps<{
  src: string
  options: {
    controls: {
      mode: 'youtube' | 'voix'
      display: boolean
      position: string
    }
    style: {
      respectHeight: boolean
      respectWidth: boolean
      aspectRatio: string
    }
    autoplay: boolean
    loop: boolean
    fullscreen: boolean
    volume: number
    mute: boolean
  }
  width: number | undefined
  height: number | undefined
}>()

// State
let player = {} as any
const video = ref()
const paused = ref(true)
const muted = ref(false)
const showControls = ref(false)

// Computed
const { onLoaded } = useScriptYouTubePlayer()
const { idle, reset } = useIdle(1000)
const youtubeId = computed(() => {
  const regex = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]{11})/
  const match = props.src.match(regex)
  return match ? match[1] : null
})
const youtubeControls = computed(() => {
  return props.options.controls.mode === 'youtube' && props.options.controls.display
})
const videoStyles = computed(() => {
  const styles = {
    aspectRatio: props.options.style.aspectRatio ? `aspect-ratio: ${props.options.style.aspectRatio}; ` : '',
    width: props.options.style.respectWidth ? `width: ${props.width}px; ` : '',
    height: props.options.style.respectHeight ? `height: ${props.height}px; ` : '',
  }

  return styles.aspectRatio + styles.width + styles.height
})
const volume = ref(0)

// Methods
function play() {
  paused.value = false
  player.playVideo()
}
function pause() {
  paused.value = true
  player.pauseVideo()
}
function mute() {
  muted.value = true
  player.mute()
}
function unMute() {
  muted.value = false
  player.unMute()
}
function setVolume({ value, init = false }: { value: number, init?: boolean }) {
  volume.value = value
  player.setVolume(value)

  if (!init && Number(value) === 0)
    mute()
  else
    unMute()
}
function fullscreen() {
  if (player.g.requestFullscreen) {
    player.g.requestFullscreen()
  }
  else if (player.g.mozRequestFullScreen) {
    player.g.mozRequestFullScreen()
  }
  else if (player.g.webkitRequestFullscreen) {
    player.g.webkitRequestFullscreen()
  }
  else if (player.g.msRequestFullscreen) {
    player.g.msRequestFullscreen()
  }
}

// Watchers
watch(idle, (value) => {
  if (value) {
    showControls.value = false
  }
  else {
    showControls.value = true
  }
})

// Lifecycle
onLoaded(async ({ YT }) => {
  // Wait For YouTube API
  const YouTubePlayer = await YT
  await new Promise<void>((resolve) => {
    if (typeof YT.Player === 'undefined')
      YouTubePlayer.ready(resolve)
    else
      resolve()
  })

  // Load YouTube API
  player = new YT.Player(video.value, {
    videoId: youtubeId.value,
    playerVars: {
      playsinline: 1, // Play inline or fullscreen on iOS
      controls: youtubeControls.value ? 1 : 0,
      loop: props.options.loop ? 1 : 0,
      fs: props.options.fullscreen ? 1 : 0, // Allow Fullscreen
      playlist: youtubeId.value,
      width: 'auto',
      height: 'auto',
      rel: 0,
    },
  })

  // Set Default Options
  setTimeout(() => {
    setVolume({ value: props.options.volume, init: true })

    if (props.options.mute)
      mute()
    if (props.options.autoplay)
      play()

    const iframe = document.querySelector('iframe')
    if (!iframe)
      return

    iframe.onload = () => {
      if (!iframe.contentDocument)
        return
      const button = iframe.contentDocument.querySelector('.ytp-large-play-button') as HTMLElement
      if (!button)
        return
      button.style.display = 'none'
    }
  }, 1000)
})
</script>

<template>
  <div class="relative">
    <div :style="videoStyles" v-bind="$attrs">
      <div
        ref="video"
        style="width: 100%; height: 100%"
      />
    </div>

    <slot v-if="options.controls.mode === 'voix' && options.controls.display" :play="play" :pause="pause" :paused="paused" :mute="mute" :un-mute="unMute" :muted="muted" :volume="volume" :set-volume="setVolume" :fullscreen="fullscreen" :allow-fullscreen="options.fullscreen">
      <div
        class="flex absolute top-0 left-0 h-full w-full text-white duration-200"
        :class="{ 'opacity-0': !showControls, 'opacity-100': showControls || paused }"
        @mouseenter="showControls = true; reset()"
        @mousemove="showControls = true; reset()"
        @mouseleave="showControls = false"
      >
        <VoixVideoControls :paused="paused" :play="play" :pause="pause" :mute="mute" :un-mute="unMute" :muted="muted" :volume="volume" :set-volume="setVolume" :fullscreen="fullscreen" :allow-fullscreen="options.fullscreen" :controls-position="options.controls.position" />
      </div>
    </slot>
  </div>
</template>
